import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm({ dark = false }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      toast.success(res.data?.message || "Thank you — we'll be in touch.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls = `font-display text-xs uppercase tracking-[0.2em] ${dark ? "text-slate-400" : "text-slate-500"}`;
  const inputBase = `w-full bg-transparent border-b py-3 text-base outline-none transition-colors placeholder:text-slate-400 ${
    dark
      ? "border-white/20 text-white focus:border-[#4b6bd6]"
      : "border-slate-300 text-[#0A1128] focus:border-[#1D4ED8]"
  }`;

  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8" noValidate>
      <div className="grid gap-8 sm:grid-cols-2">
        <div>
          <label htmlFor="cf-name" className={labelCls}>
            Name*
          </label>
          <input
            id="cf-name"
            name="name"
            data-testid="contact-name"
            value={form.name}
            onChange={onChange}
            placeholder="Your name"
            className={inputBase}
            autoComplete="name"
          />
        </div>
        <div>
          <label htmlFor="cf-email" className={labelCls}>
            Email*
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            data-testid="contact-email"
            value={form.email}
            onChange={onChange}
            placeholder="you@company.com"
            className={inputBase}
            autoComplete="email"
          />
        </div>
      </div>
      <div>
        <label htmlFor="cf-phone" className={labelCls}>
          Phone (optional)
        </label>
        <input
          id="cf-phone"
          name="phone"
          data-testid="contact-phone"
          value={form.phone}
          onChange={onChange}
          placeholder="Best number to reach you"
          className={inputBase}
          autoComplete="tel"
        />
      </div>
      <div>
        <label htmlFor="cf-message" className={labelCls}>
          Message*
        </label>
        <textarea
          id="cf-message"
          name="message"
          data-testid="contact-message"
          value={form.message}
          onChange={onChange}
          rows={4}
          placeholder="Tell us about your project or the work you need"
          className={`${inputBase} resize-none`}
        />
      </div>

      <button
        type="submit"
        data-testid="contact-submit"
        disabled={loading}
        className={`group inline-flex w-full items-center justify-center gap-3 px-7 py-4 font-display text-sm uppercase tracking-widest transition-colors disabled:opacity-70 sm:w-auto ${
          dark ? "bg-white text-[#0A1128] hover:bg-slate-200" : "bg-[#0A1128] text-white hover:bg-[#1D4ED8]"
        }`}
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
        {loading ? "Sending..." : done ? "Send another" : "Request a Call Back"}
      </button>
      <p className={`text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>
        We aim to respond within 1 business day. By submitting you agree to our{" "}
        <a href="/privacy-policy" className="underline underline-offset-2">
          Privacy Policy
        </a>
        .
      </p>
    </form>
  );
}
